/**
 * Channel-promo spam patterns (`spec.md` §11.9 step 3) and the validator for the
 * operator-supplied `INDEX_SPAM_PATTERNS` environment value (`spec.md` §14).
 *
 * PURE MODULE — no `node:*` imports. It is consumed by the indexer (Node) and, via
 * `@bl/shared`, by the browser bundle.
 *
 * Two of the regexes in the spec's §11.9 step-3 bullet list DO NOT MATCH the corpus the
 * same section requires them to strip, so they are corrected here and the deviation is
 * spelled out rather than silently smoothed over:
 *
 *   spec: /[\(\[\{]\s*(?:@[A-Za-z0-9_]{4,32}|t\.me\/\S+|скачано\s+с\S*|by\s+@\S+)\s*[\)\]\}]\s*$/gi
 *
 *   `скачано\s+с\S*` cannot match `[скачано с xxx]`: after `с` the `\S*` stops at the
 *   space, and the required closing bracket is then `x`, not `]`. The alternative is
 *   widened to `скачано[^)\]}]*` so the form the spec's own fixture list names
 *   (`[скачано с xxx]`) is actually stripped.
 *
 *   `t\.me\/\S+` only matches `(t.me/xxx)` by backtracking `\S+` off the `)`. It is
 *   rewritten as `[^\s)\]}]*` so the bracket is excluded structurally instead of by
 *   luck, and `telegram.me` / a `https://` prefix are accepted too, which the prose
 *   bullet above it asks for.
 */
/** A leading or trailing `@handle` token. The captured boundary character is kept, so
 *  `(@promo)` collapses to `()` and is then removed by the empty-pair rule. */
export const SPAM_HANDLE_RE = /(^|[\s\-_|·•(\[])@[A-Za-z0-9_]{4,32}\b/g;
/** A trailing `t.me/…` / `telegram.me/…` URL, with or without scheme. */
export const SPAM_TME_TAIL_RE = /(?:https?:\/\/)?(?:www\.)?(?:t|telegram)\.me\/[^\s)\]}]*\s*$/gi;
/** A trailing bracketed promo of any bracket flavour. */
export const SPAM_BRACKET_PROMO_RE = /[([{]\s*(?:@[A-Za-z0-9_]{4,32}|(?:https?:\/\/)?(?:www\.)?(?:t|telegram)\.me\/[^\s)\]}]*|скачано[^)\]}]*|качай[^)\]}]*|by\s+@[^\s)\]}]+)\s*[)\]}]\s*$/gi;
/** An empty bracket pair left behind by an inner removal. */
export const SPAM_EMPTY_BRACKETS_RE = /[([{]\s*[)\]}]/g;
/** Separator debris at the tail (`spec.md` §11.9 step 3, bullet 4). */
export const SPAM_DEBRIS_TAIL_RE = /[\s\-_|·•]{2,}$/g;
/** Separator debris at the head. */
export const SPAM_DEBRIS_HEAD_RE = /^[\s\-_|·•]+/g;
/**
 * The built-in rules, applied in this order on every pass of `stripSpam`.
 * The bracketed-promo rule runs first: it consumes `[@handle]` whole, which keeps the
 * handle rule from leaving a bracket pair behind on the commonest form of all.
 */
export const DEFAULT_SPAM_RULES = Object.freeze([
    Object.freeze({ name: 'bracket-promo', pattern: SPAM_BRACKET_PROMO_RE, replacement: ' ' }),
    Object.freeze({ name: 'handle', pattern: SPAM_HANDLE_RE, replacement: '$1' }),
    Object.freeze({ name: 'tme-tail', pattern: SPAM_TME_TAIL_RE, replacement: ' ' }),
]);
/** The default pattern list, in rule order. */
export const DEFAULT_SPAM_PATTERNS = Object.freeze(DEFAULT_SPAM_RULES.map((r) => r.pattern));
/** Longest accepted source for an operator-supplied pattern. */
export const SPAM_PATTERN_MAX_LENGTH = 200;
/** Largest accepted `INDEX_SPAM_PATTERNS` array. */
export const SPAM_PATTERN_MAX_COUNT = 32;
/** Thrown by `validateSpamPattern` / `parseSpamPatterns`. Carries the offending source. */
export class SpamPatternError extends Error {
    pattern;
    index;
    constructor(message, pattern, index = -1) {
        super(message);
        this.name = 'SpamPatternError';
        this.pattern = pattern;
        this.index = index;
    }
}
const isUnboundedQuantifier = (src, i) => {
    const ch = src[i];
    if (ch === '*' || ch === '+')
        return true;
    if (ch !== '{')
        return false;
    // `{n,}` is unbounded; `{n}` and `{n,m}` are not.
    const close = src.indexOf('}', i);
    if (close === -1)
        return false;
    return /^\{\d+,\}$/.test(src.slice(i, close + 1));
};
/**
 * Detects a quantified group whose body itself contains an unbounded quantifier —
 * `(a+)+`, `(\S+)*`, `([^x]*){2,}` — the shape behind every classic catastrophic
 * backtracking blow-up. Deliberately a syntactic over-approximation: it is cheap, has
 * no false negatives for the nested-quantifier family, and the cost of a false positive
 * is that an operator has to rewrite one pattern.
 */
export function hasNestedUnboundedQuantifier(source) {
    const openIsUnbounded = [];
    let inClass = false;
    for (let i = 0; i < source.length; i++) {
        const ch = source[i];
        if (ch === undefined)
            break;
        if (ch === '\\') {
            i += 1;
            continue;
        }
        if (inClass) {
            if (ch === ']')
                inClass = false;
            continue;
        }
        if (ch === '[') {
            inClass = true;
            continue;
        }
        if (ch === '(') {
            openIsUnbounded.push(false);
            continue;
        }
        if (ch === ')') {
            const bodyHadUnbounded = openIsUnbounded.pop() ?? false;
            const quantified = isUnboundedQuantifier(source, i + 1);
            if (bodyHadUnbounded && quantified)
                return true;
            if (openIsUnbounded.length > 0 && (bodyHadUnbounded || quantified)) {
                openIsUnbounded[openIsUnbounded.length - 1] = true;
            }
            continue;
        }
        if (isUnboundedQuantifier(source, i) && openIsUnbounded.length > 0) {
            openIsUnbounded[openIsUnbounded.length - 1] = true;
        }
    }
    return false;
}
/**
 * Compiles one operator-supplied pattern source into a global, case-insensitive RegExp.
 * Throws `SpamPatternError` when the source is empty, over-long, uncompilable, able to
 * match the empty string (a global empty match rewrites every position), or
 * catastrophically backtracking.
 */
export function validateSpamPattern(source, index = -1) {
    if (typeof source !== 'string') {
        throw new SpamPatternError('spam pattern must be a string', String(source), index);
    }
    if (source.trim() === '') {
        throw new SpamPatternError('spam pattern must not be empty', source, index);
    }
    if (source.length > SPAM_PATTERN_MAX_LENGTH) {
        throw new SpamPatternError(`spam pattern must be at most ${SPAM_PATTERN_MAX_LENGTH} characters, got ${source.length}`, source, index);
    }
    if (hasNestedUnboundedQuantifier(source)) {
        throw new SpamPatternError('spam pattern contains a nested unbounded quantifier and may backtrack catastrophically', source, index);
    }
    let probe;
    try {
        probe = new RegExp(source, 'i');
    }
    catch (err) {
        const reason = err instanceof Error ? err.message : String(err);
        throw new SpamPatternError(`spam pattern does not compile: ${reason}`, source, index);
    }
    if (probe.test('')) {
        throw new SpamPatternError('spam pattern must not match the empty string', source, index);
    }
    return new RegExp(source, 'gi');
}
/**
 * Parses the `INDEX_SPAM_PATTERNS` environment value: a JSON array of regex source
 * strings. `undefined`, `null` and an empty/blank string all mean "none configured".
 */
export function parseSpamPatterns(raw) {
    if (raw === undefined || raw === null || raw.trim() === '')
        return [];
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch (err) {
        const reason = err instanceof Error ? err.message : String(err);
        throw new SpamPatternError(`INDEX_SPAM_PATTERNS is not valid JSON: ${reason}`, raw);
    }
    if (!Array.isArray(parsed)) {
        throw new SpamPatternError('INDEX_SPAM_PATTERNS must be a JSON array of strings', raw);
    }
    if (parsed.length > SPAM_PATTERN_MAX_COUNT) {
        throw new SpamPatternError(`INDEX_SPAM_PATTERNS must hold at most ${SPAM_PATTERN_MAX_COUNT} patterns, got ${parsed.length}`, raw);
    }
    const out = [];
    for (let i = 0; i < parsed.length; i++) {
        const entry = parsed[i];
        if (typeof entry !== 'string') {
            throw new SpamPatternError(`INDEX_SPAM_PATTERNS[${i}] must be a string, got ${typeof entry}`, String(entry), i);
        }
        out.push(validateSpamPattern(entry, i));
    }
    return out;
}
//# sourceMappingURL=spam.js.map